import React from 'react';
import { BreadcrumbItemProps } from '../components/Breadcrumbs/Breadcrumbs';
import { ArticleProps } from '../views/Article/Article';
import { ArticleRichText } from '../components/ArticleRichText/ArticleRichText';
import { AuthorTransformer } from '@jbhi-fi/components/src/Contentful/transformers/AuthorTransformer';
import { format, parseISO } from 'date-fns';
import { ContentfulArticle, ContentfulCompetition, EntryType } from '@jbhi-fi/apis/src/contentful/types';
import { AssetTransformer } from '@jbhi-fi/components/src/Contentful/transformers/AssetTransformer';
import { CardTransformer } from '@jbhi-fi/components/src/Contentful/transformers/CardTransformer';
import { palette } from '@jbhi-fi/lanyard-ui/themes';
import { YouTubeTransformer } from '@jbhi-fi/components/src/Contentful/transformers/YouTubeTransformer';
import { buildContentfulApiImgSrc } from '@jbhi-fi/components/src/ContentfulImage/buildContentfulApiImgSrc';

export const contentfulApiArticleToArticleProps = (contentfulApiArticle: ContentfulArticle): ArticleProps => {
  const { fields, sys } = contentfulApiArticle;
  const coverImage = fields?.heroImage?.fields ? AssetTransformer.toImageProps(fields.heroImage) : null;
  const publishedDate = fields?.publishedDate || sys.createdAt;
  return {
    articleHeader: {
      coverImgSrc: coverImage?.src ?? '',
      coverImgAlt: coverImage?.alt ?? '',
      videoSrc: fields?.heroVideo?.fields?.url,
      youTubeProps: fields?.heroVideo
        ? {
            ...YouTubeTransformer.toYouTubePlayerProps(fields.heroVideo),
            ...(coverImage?.src
              ? {
                  landscapeThumbnailUrl: buildContentfulApiImgSrc(coverImage.src),
                }
              : {}),
          }
        : undefined,
      videoPlatform: fields?.heroVideo?.fields?.url ? 'youTube' : undefined,
    },
    articleBlock: {
      intro: fields?.intro ?? '',
      content: fields?.mainBody ? <ArticleRichText document={fields.mainBody} /> : null,
      date: format(parseISO(publishedDate), 'd MMM yyyy'),
      creatorProfile: fields?.author ? AuthorTransformer.toUserProfileProps(fields.author) : null,
      heading: fields?.heading ?? '',
      tag: CardTransformer.toTagProps(contentfulApiArticle, { includeSubCategoryHref: true }),
    },
    breadcrumbs: {
      colorHex: palette.primary.neutral,
      items: articleToBreadcrumbs(contentfulApiArticle),
    },
    relatedEntries: {
      heading: fields?.parentPage?.fields ? `More from ${fields?.parentPage.fields.heading}` : '',
      contentType: EntryType.article,
      filter: fields?.parentPage?.sys
        ? {
            'fields.parentPage.sys.id': fields?.parentPage.sys.id,
            'sys.id[nin]': contentfulApiArticle.sys.id,
          }
        : null,
    },
  };
};

export const DEFAULT_CONTENT_HUB_URL = '/blogs/stack';
export const DEFAULT_CONTENT_HUB_LABEL = 'STACK Hub';

export const articleToBreadcrumbs = (
  contentfulApiArticle: ContentfulArticle | ContentfulCompetition
): Array<BreadcrumbItemProps> => {
  const { fields } = contentfulApiArticle;
  const breadcrumbs: Array<BreadcrumbItemProps> = [
    { href: '/', label: 'Home' },
    {
      href: window.contentHubUrl ?? DEFAULT_CONTENT_HUB_URL,
      label: window.contentHubLabel ?? DEFAULT_CONTENT_HUB_LABEL,
    },
  ];

  if (fields?.parentPage?.fields !== undefined) {
    breadcrumbs.push({
      href: `/blogs/${fields?.parentPage?.fields?.categoryPage?.fields?.urlSlug}`,
      label: fields?.parentPage?.fields?.categoryPage?.fields?.heading ?? '',
    });

    breadcrumbs.push({
      href: fields.parentPage.fields.urlSlug,
      label: fields.parentPage.fields.heading,
    });
  }

  breadcrumbs.push({
    href: fields?.urlSlug ?? '',
    label: fields?.heading ?? '',
    isActive: true,
  });

  return breadcrumbs;
};
